import { format, isBefore, isSameDay, startOfDay } from 'date-fns';

export type CommandScheduleMode = 'one_time' | 'recurring';
export type CommandRecurringFrequency = 'daily' | 'weekly' | 'monthly';

export function buildRunAtFromLocalParts(selectedDay: Date, hour: number, minute: number): Date {
  return new Date(
    selectedDay.getFullYear(),
    selectedDay.getMonth(),
    selectedDay.getDate(),
    hour,
    minute,
    0,
    0,
  );
}

export function parseHm(hhmm: string): { h: number; m: number } {
  const [sh, sm] = hhmm.split(':');
  const h = Math.min(23, Math.max(0, Number.parseInt(sh ?? '0', 10) || 0));
  const m = Math.min(59, Math.max(0, Number.parseInt(sm ?? '0', 10) || 0));
  return { h, m };
}

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function clampTimeIfNeeded(day: Date, hhmm: string): string {
  const now = new Date();
  if (!isSameDay(day, now)) return hhmm;
  const { h, m } = parseHm(hhmm);
  const candidate = buildRunAtFromLocalParts(day, h, m);
  if (candidate.getTime() >= now.getTime()) return hhmm;
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
}

export function formatHm12(hhmm: string): string {
  const { h, m } = parseHm(hhmm);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${pad2(m)} ${period}`;
}

export function buildCronExpression(
  frequency: CommandRecurringFrequency,
  hhmm: string,
  weekDays: number[],
  dayOfMonth: number,
): string {
  const { h, m } = parseHm(hhmm);
  if (frequency === 'daily') {
    if (weekDays.length > 0 && weekDays.length < 7) {
      return `${m} ${h} * * ${weekDays.join(',')}`;
    }
    return `${m} ${h} * * *`;
  }
  if (frequency === 'weekly') {
    const days = weekDays.length > 0 ? weekDays.join(',') : '1';
    return `${m} ${h} * * ${days}`;
  }
  const dom = Math.min(31, Math.max(1, dayOfMonth));
  return `${m} ${h} ${dom} * *`;
}

export function computeNextDailyRun(hour: number, minute: number, from = new Date()): Date {
  const candidate = new Date(from);
  candidate.setSeconds(0, 0);
  candidate.setHours(hour, minute, 0, 0);
  if (candidate.getTime() <= from.getTime()) {
    candidate.setDate(candidate.getDate() + 1);
  }
  return candidate;
}

export function computeNextWeeklyRun(
  weekDays: number[],
  hour: number,
  minute: number,
  from = new Date(),
): Date {
  const days = weekDays.length > 0 ? [...weekDays].sort((a, b) => a - b) : [1];
  for (let offset = 0; offset < 14; offset += 1) {
    const candidate = new Date(from);
    candidate.setDate(from.getDate() + offset);
    candidate.setSeconds(0, 0);
    candidate.setHours(hour, minute, 0, 0);
    if (days.includes(candidate.getDay()) && candidate.getTime() > from.getTime()) {
      return candidate;
    }
  }
  const fallback = new Date(from);
  fallback.setDate(from.getDate() + 7);
  fallback.setHours(hour, minute, 0, 0);
  return fallback;
}

export function computeNextMonthlyRun(
  dayOfMonth: number,
  hour: number,
  minute: number,
  from = new Date(),
): Date {
  const dom = Math.min(31, Math.max(1, dayOfMonth));
  for (let monthOffset = 0; monthOffset < 14; monthOffset += 1) {
    const candidate = new Date(from.getFullYear(), from.getMonth() + monthOffset, 1, hour, minute, 0, 0);
    const lastDay = new Date(candidate.getFullYear(), candidate.getMonth() + 1, 0).getDate();
    candidate.setDate(Math.min(dom, lastDay));
    if (candidate.getTime() > from.getTime()) {
      return candidate;
    }
  }
  const fallback = new Date(from.getFullYear(), from.getMonth() + 1, dom, hour, minute, 0, 0);
  return fallback;
}

export function computeNextRecurringRun(
  frequency: CommandRecurringFrequency,
  hhmm: string,
  weekDays: number[],
  dayOfMonth: number,
  from = new Date(),
): Date {
  const { h, m } = parseHm(hhmm);
  if (frequency === 'daily') {
    if (weekDays.length > 0 && weekDays.length < 7) {
      return computeNextWeeklyRun(weekDays, h, m, from);
    }
    return computeNextDailyRun(h, m, from);
  }
  if (frequency === 'weekly') return computeNextWeeklyRun(weekDays, h, m, from);
  return computeNextMonthlyRun(dayOfMonth, h, m, from);
}

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function formatWeekdayList(weekDays: number[]): string {
  const sorted = [...weekDays].sort((a, b) => a - b);
  if (sorted.length === 5 && sorted.join(',') === '1,2,3,4,5') {
    return 'weekday';
  }
  if (sorted.length === 7) return 'day';
  return sorted.map((d) => WEEKDAY_NAMES[d]).join(', ');
}

export function buildRecurringSummaryLabel(
  frequency: CommandRecurringFrequency,
  hhmm: string,
  weekDays: number[],
  dayOfMonth = 1,
): string {
  const timeLabel = formatHm12(hhmm);
  if (frequency === 'daily') {
    const label = formatWeekdayList(weekDays);
    if (label === 'weekday') return `Every weekday at ${timeLabel}`;
    if (label === 'day') return `Every day at ${timeLabel}`;
    return `Every ${label} at ${timeLabel}`;
  }
  if (frequency === 'weekly') {
    const label = formatWeekdayList(weekDays);
    return `Every week on ${label} at ${timeLabel}`;
  }
  const ordinal =
    dayOfMonth % 100 >= 11 && dayOfMonth % 100 <= 13
      ? `${dayOfMonth}th`
      : `${dayOfMonth}${['th', 'st', 'nd', 'rd'][dayOfMonth % 10] ?? 'th'}`;
  return `Every month on the ${ordinal} at ${timeLabel}`;
}

export function localHmToUtcHm(hhmm: string): string {
  const { h, m } = parseHm(hhmm);
  const now = new Date();
  const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
  return `${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}`;
}

/** Puts `runAt`'s UTC hour/minute into a cron so the backend always gets UTC. */
export function cronExpressionWithUtcTime(
  cronExpression: string,
  runAt: Date,
): string {
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) return cronExpression;
  parts[0] = String(runAt.getUTCMinutes());
  parts[1] = String(runAt.getUTCHours());
  return parts.join(' ');
}

export function isDateDisabledForSchedule(date: Date): boolean {
  return isBefore(startOfDay(date), startOfDay(new Date()));
}

export function formatOneTimeRunLabel(runAt: Date): string {
  return `Once on ${format(runAt, 'EEE, MMM d')} at ${format(runAt, 'h:mm a')}`;
}

export function formatNextRunLabel(runAt: Date): string {
  return format(runAt, 'EEE, MMM d, h:mm a');
}
